import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useApi } from "../../hooks/useApi";
import { Badge } from "../../components/ui/badge";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../../components/ui/select";
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "../../components/ui/alert-dialog";
import { Loader2 } from "lucide-react";

const OFFERS = [
  { key: "gold_monthly", label: "Gold — Monthly ($19)", tier: "gold", interval: "month" },
  { key: "gold_annual", label: "Gold — Annual ($182.40)", tier: "gold", interval: "year" },
  { key: "gold_pro_monthly", label: "Gold Pro — Monthly ($100)", tier: "gold_pro", interval: "month" },
  { key: "gold_pro_annual", label: "Gold Pro — Annual ($960)", tier: "gold_pro", interval: "year" },
];

function fmtMoney(cents) {
  if (cents == null) return "—";
  return `$${(cents / 100).toFixed(2)}`;
}
function fmtDate(ts) {
  if (!ts) return "—";
  return new Date(ts * 1000).toLocaleDateString();
}

function PlanChangeDialog({ row, onDone }) {
  const api = useApi();
  const [toOffer, setToOffer] = useState("");
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [applying, setApplying] = useState(false);

  const loadPreview = async (offerKey) => {
    setToOffer(offerKey);
    setPreview(null);
    if (!offerKey) return;
    setLoadingPreview(true);
    try {
      const res = await api.post(`/admin/subscriptions/${row.entitlement_id}/preview-change`, { to_offer_key: offerKey });
      setPreview(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not preview this change.");
    } finally {
      setLoadingPreview(false);
    }
  };

  const apply = async () => {
    setApplying(true);
    try {
      const action = preview?.is_upgrade ? "upgrade" : "downgrade";
      await api.post(`/admin/subscriptions/${row.entitlement_id}/${action}`, { to_offer_key: toOffer });
      toast.success(preview?.is_upgrade ? "Upgrade applied immediately." : "Downgrade scheduled for the next renewal.");
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not apply this change.");
    } finally {
      setApplying(false);
    }
  };

  const availableOffers = OFFERS.filter((o) => !(o.tier === row.product_type && o.interval === row.interval) && o.key !== row.offer_key);

  return (
    <AlertDialog onOpenChange={(open) => { if (!open) { setToOffer(""); setPreview(null); } }}>
      <AlertDialogTrigger asChild>
        <button data-testid={`admin-sub-change-plan-${row.entitlement_id}`} className="text-xs uppercase tracking-wide border border-white/20 px-3 py-1.5 hover:border-white/50">
          Change Plan
        </button>
      </AlertDialogTrigger>
      <AlertDialogContent className="bg-surface border-white/10 text-white max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle>Change {row.email}'s plan</AlertDialogTitle>
          <AlertDialogDescription className="text-white/60">
            Upgrades apply immediately with disclosed proration. Downgrades are scheduled to take effect at the next renewal via a Stripe Subscription Schedule.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <Select value={toOffer} onValueChange={loadPreview}>
          <SelectTrigger data-testid="admin-sub-change-plan-select" className="bg-transparent border-white/20"><SelectValue placeholder="Choose a target plan" /></SelectTrigger>
          <SelectContent>
            {availableOffers.map((o) => <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>)}
          </SelectContent>
        </Select>
        {loadingPreview && <p className="text-xs text-white/50 flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" /> Checking with Stripe…</p>}
        {preview && (
          <div className="text-xs text-white/70 border border-white/10 p-3 space-y-1" data-testid="admin-sub-change-plan-preview">
            <p>{preview.is_upgrade ? "Immediate upgrade" : "Scheduled downgrade"} to {preview.label}.</p>
            {preview.is_upgrade && <p>Estimated amount due now: <span className="text-white">{fmtMoney(preview.estimated_amount_due_cents)}</span></p>}
            {!preview.is_upgrade && <p>Takes effect at the current period's renewal — no charge today.</p>}
          </div>
        )}
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction data-testid="admin-sub-change-plan-confirm" disabled={!preview || applying} onClick={apply}>
            {applying ? "Applying…" : !preview ? "Select a Plan First" : preview.is_upgrade ? "Apply Upgrade Now" : "Schedule Downgrade"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function CancelDialog({ row, onChanged }) {
  const api = useApi();
  const [busy, setBusy] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [open, setOpen] = useState(false);

  const act = async (atPeriodEnd) => {
    setBusy(true);
    try {
      await api.post(`/admin/subscriptions/${row.entitlement_id}/cancel`, { at_period_end: atPeriodEnd });
      toast.success(atPeriodEnd ? "Will cancel at period end." : "Cancelled immediately.");
      setOpen(false);
      setConfirmText("");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={(next) => { setOpen(next); if (!next) setConfirmText(""); }}>
      <AlertDialogTrigger asChild>
        <button data-testid={`admin-sub-cancel-${row.entitlement_id}`} className="text-xs uppercase tracking-wide border border-red-400/40 text-red-300 px-3 py-1.5 hover:border-red-400">
          Cancel Subscription
        </button>
      </AlertDialogTrigger>
      <AlertDialogContent className="bg-surface border-white/10 text-white">
        <AlertDialogHeader>
          <AlertDialogTitle>Cancel {row.email}'s subscription?</AlertDialogTitle>
          <AlertDialogDescription className="text-white/60">Choose when access should end.</AlertDialogDescription>
        </AlertDialogHeader>
        <div className="space-y-3">
          <button data-testid={`admin-sub-cancel-period-end-${row.entitlement_id}`} disabled={busy} onClick={() => act(true)}
            className="w-full text-xs uppercase tracking-wide border border-white/20 px-4 py-2 hover:border-white/50 disabled:opacity-40">
            Cancel At Period End
          </button>
          <div className="border-t border-white/10 pt-3">
            <p className="text-xs text-red-300/80 mb-2">Immediate cancellation revokes access right now and cannot be undone. Type CANCEL to confirm.</p>
            <input
              data-testid={`admin-sub-cancel-immediate-confirm-input-${row.entitlement_id}`}
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="Type CANCEL"
              className="w-full bg-transparent border border-red-400/30 px-3 py-2 text-sm mb-2"
            />
            <button
              data-testid={`admin-sub-cancel-immediate-${row.entitlement_id}`}
              disabled={busy || confirmText.trim().toUpperCase() !== "CANCEL"}
              onClick={() => act(false)}
              className="w-full text-xs uppercase tracking-wide border border-red-400 text-red-300 px-4 py-2 hover:bg-red-500/10 disabled:opacity-30"
            >
              Cancel Immediately
            </button>
          </div>
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel>Keep Subscription</AlertDialogCancel>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function SubscriptionRow({ row, onChanged }) {
  const api = useApi();
  const [busy, setBusy] = useState(false);

  const act = async (action, body) => {
    setBusy(true);
    try {
      await api.post(`/admin/subscriptions/${row.entitlement_id}/${action}`, body);
      toast.success("Done.");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed.");
    } finally {
      setBusy(false);
    }
  };

  const linked = Boolean(row.stripe_subscription_id);
  const isModifiable = ["active", "trialing", "past_due"].includes(row.stripe_status);

  return (
    <div className="border border-white/10 p-4 mb-3" data-testid={`admin-sub-row-${row.entitlement_id}`}>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
        <div className="min-w-0">
          <p className="text-sm font-medium truncate">{row.email || "Unknown"}</p>
          <p className="text-xs text-white/50 uppercase">{row.offer_label || row.product_type.replace("_", " ")} · {row.interval || "—"}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {row.stripe_error ? (
            <Badge className="bg-red-500/15 text-red-300 border-red-500/40" data-testid={`admin-sub-status-${row.entitlement_id}`}>Stripe unreachable</Badge>
          ) : (
            <Badge variant="outline" data-testid={`admin-sub-status-${row.entitlement_id}`}>{row.stripe_status || row.status}</Badge>
          )}
          {row.cancel_at_period_end && <Badge className="bg-yellow-500/10 text-yellow-300 border-yellow-500/30">Cancels at renewal</Badge>}
          {row.payment_issue && <Badge className="bg-red-500/15 text-red-300 border-red-500/40">Payment {row.payment_issue}</Badge>}
          {row.pending_plan_change && <Badge className="bg-blue-500/10 text-blue-300 border-blue-500/30">Pending → {row.pending_plan_change.to_offer_key}</Badge>}
        </div>
      </div>
      <p className="text-xs text-white/40 mb-3">
        {row.stripe_error
          ? `Could not load live billing details: ${row.stripe_error}`
          : linked ? `Renews/ends ${fmtDate(row.current_period_end)} · ${fmtMoney(row.amount_cents)}` : "No linked Stripe subscription — manually granted entitlement."}
      </p>
      {linked && !row.stripe_error && isModifiable && (
        <div className="flex flex-wrap gap-2">
          <PlanChangeDialog row={row} onDone={onChanged} />
          {row.payment_issue && (
            <button data-testid={`admin-sub-retry-payment-${row.entitlement_id}`} onClick={() => act("retry-payment")} disabled={busy}
              className="text-xs uppercase tracking-wide border border-white/20 px-3 py-1.5 hover:border-white/50 disabled:opacity-40">
              Retry Payment
            </button>
          )}
          {row.pending_plan_change && (
            <button data-testid={`admin-sub-cancel-pending-${row.entitlement_id}`} onClick={() => act("cancel-pending-change")} disabled={busy}
              className="text-xs uppercase tracking-wide border border-white/20 px-3 py-1.5 hover:border-white/50 disabled:opacity-40">
              Cancel Scheduled Change
            </button>
          )}
          {!row.cancel_at_period_end ? (
            <CancelDialog row={row} onChanged={onChanged} />
          ) : (
            <Badge variant="outline">Already set to cancel at renewal</Badge>
          )}
        </div>
      )}
      {linked && !row.stripe_error && !isModifiable && (
        <Badge variant="outline" className="text-white/40" data-testid={`admin-sub-no-actions-${row.entitlement_id}`}>No actions available for a {row.stripe_status || "unrecognized"} subscription</Badge>
      )}
    </div>
  );
}

export default function AdminSubscriptions() {
  const api = useApi();
  const qc = useQueryClient();
  const [qInput, setQInput] = useState("");
  const [q, setQ] = useState("");
  const [product, setProduct] = useState("all");
  const [status, setStatus] = useState("all");
  const [page, setPage] = useState(1);
  const mountedRef = useRef(false);

  useEffect(() => {
    if (!mountedRef.current) { mountedRef.current = true; return; }
    const t = setTimeout(() => { setQ(qInput); setPage(1); }, 400);
    return () => clearTimeout(t);
  }, [qInput]);

  const { data, isLoading } = useQuery({
    queryKey: ["admin-subscriptions", q, product, status, page],
    queryFn: async () => (await api.get("/admin/subscriptions", {
      params: {
        q, page, page_size: 25,
        product: product === "all" ? "" : product,
        status: status === "all" ? "" : status,
      },
    })).data,
  });

  const refresh = () => qc.invalidateQueries({ queryKey: ["admin-subscriptions"] });
  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.page_size)) : 1;

  return (
    <div data-testid="admin-subscriptions-page">
      <h1 className="font-display text-display-xs mb-2">Gold / Gold Pro Subscriptions</h1>
      <p className="text-xs text-foreground/60 mb-6">Stripe is the source of truth for billing state. Entries with no linked Stripe subscription are manually-granted entitlements and have nothing to change.</p>

      <div className="flex flex-wrap gap-3 mb-6">
        <input
          data-testid="admin-subscriptions-search-input"
          placeholder="Search by email…"
          value={qInput}
          onChange={(e) => setQInput(e.target.value)}
          className="bg-transparent border border-white/20 px-3 py-2 text-sm min-w-[220px]"
        />
        <Select value={product} onValueChange={(v) => { setProduct(v); setPage(1); }}>
          <SelectTrigger data-testid="admin-subscriptions-product-filter" className="w-40 bg-transparent border-white/20"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any plan</SelectItem>
            <SelectItem value="gold">Gold</SelectItem>
            <SelectItem value="gold_pro">Gold Pro</SelectItem>
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={(v) => { setStatus(v); setPage(1); }}>
          <SelectTrigger data-testid="admin-subscriptions-status-filter" className="w-40 bg-transparent border-white/20"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="grace">Grace</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
            <SelectItem value="expired">Expired</SelectItem>
            <SelectItem value="revoked">Revoked</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {isLoading && <p className="text-white/60 text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</p>}
      {!isLoading && data?.items?.length === 0 && <p className="text-white/50 text-sm py-6" data-testid="admin-subscriptions-empty">No Gold/Gold Pro entitlements match these filters.</p>}
      {!isLoading && data?.items?.map((row) => <SubscriptionRow key={row.entitlement_id} row={row} onChanged={refresh} />)}

      {data && totalPages > 1 && (
        <div className="flex items-center gap-4 mt-6 text-xs text-white/60">
          <button data-testid="admin-subscriptions-prev-page" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="underline disabled:opacity-30">Previous</button>
          <span>Page {page} of {totalPages}</span>
          <button data-testid="admin-subscriptions-next-page" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} className="underline disabled:opacity-30">Next</button>
        </div>
      )}
    </div>
  );
}
